import axios, { CancelToken } from 'axios'
import { baseUrl } from './endpoints'
import { IS_MOCK_ENABLED } from '../../config'
import { setupAxiosCache } from './setup-axios-cache'
import { useGlobalStore } from '../store/store'

if (IS_MOCK_ENABLED) {
	import('./axiosMock')
}

const draftUrls = ['v1/draft-toolkit-categories', 'v1/draft-toolkit']

const axiosInstance = axios.create({
	baseURL: baseUrl,
	timeout: 15000,
	headers: {
		'Content-Type': 'application/json'
	},
	withCredentials: true
})

setupAxiosCache(axiosInstance, { ttl: 0 })

axiosInstance.interceptors.request.use(
	(config) => {
		const { userToken, isAdmin } = useGlobalStore.getState()
		if (userToken) {
			config.headers['X-ACCESS-AUTH-TOKEN'] = userToken
		}

		if (isAdmin && draftUrls.some((url) => config.url.startsWith(url))) {
			config.headers['X-ACCESS-AUTH-ADMIN'] = 1
		}

		return config
	},
	(error) => Promise.reject(error)
)

axiosInstance.interceptors.response.use(
	(response) => response.data,
	(error) => {
		console.error('Response error:', error)

		const { userToken, setUserTokenExpired } = useGlobalStore.getState()

		const isTimeout = error.code === 'ECONNABORTED'
		const isUnauthorized = error?.response?.status === 401
		const noResponse = !error?.response && error.message !== 'Network Error'

		if (userToken && isUnauthorized) {
			setUserTokenExpired(true)
		}
		if (isTimeout) {
			console.warn('Request timed out:', error.config?.url)
		}

		return Promise.reject(error)
	}
)

export function getRequest(url, params = {}, config = {}, cacheTtl = 0) {
	const source = CancelToken.source()
	const promise = axiosInstance.get(url, {
		params,
		...config,
		cancelToken: source.token,
		cache: {
			ttl: cacheTtl
		}
	})
	promise.cancel = () => source.cancel('Request canceled.')
	return promise
}

export function postRequest(url, data, config = {}, cacheTtl = 0) {
	const source = CancelToken.source()
	const promise = axiosInstance.post(url, data, {
		...config,
		cancelToken: source.token,
		cache: {
			ttl: cacheTtl
		}
	})
	promise.cancel = () => source.cancel('Request canceled.')
	return promise
}

export function putRequest(url, data, config = {}) {
	const source = CancelToken.source()
	const promise = axiosInstance.put(url, data, {
		...config,
		cancelToken: source.token
	})
	promise.cancel = () => source.cancel('Request canceled.')
	return promise
}

export function deleteRequest(url, config = {}) {
	const source = CancelToken.source()
	const promise = axiosInstance.delete(url, {
		...config,
		cancelToken: source.token
	})
	promise.cancel = () => source.cancel('Request canceled.')
	return promise
}
export default axiosInstance
